import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface Post {
  _id: string;
  user: {
    _id: string;
    username: string;
    name: string;
    profileImage?: string;
    email?: string;
    phone?: string;
    countryCode?: string;
    isVerified?: boolean;
    isAdmin?: boolean;
  };
  caption?: string;
  media: Array<{
    type: 'image' | 'video';
    url: string;
    thumbnail?: string;
    order: number;
  }>;
  location?: {
    name?: string;
    coordinates: [number, number];
  };
  likesCount: number;
  commentsCount: number;
  isActive: boolean;
  isSuspended?: boolean;
  suspendedUntil?: string;
  suspensionReason?: string;
  isRejected?: boolean;
  rejectionReason?: string;
  rejectedAt?: string;
  tags: string[];
  mentions: string[];
  createdAt: string;
  updatedAt: string;
}

class PostsService {
  async getPostById(id: string) {
    const response = await apiClient.get<ApiResponse<Post>>(`/posts/admin/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch post');
    }
    return response.data.data;
  }

  async updatePost(id: string, data: Partial<Post>) {
    const response = await apiClient.put<ApiResponse<Post>>(`/posts/admin/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update post');
    }
    return response.data.data;
  }

  async suspendPost(id: string, reason?: string, suspendedUntil?: string) {
    const response = await apiClient.post<ApiResponse<Post>>(`/posts/admin/${id}/suspend`, {
      reason,
      suspendedUntil,
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to suspend post');
    }
    return response.data.data;
  }

  async unsuspendPost(id: string) {
    const response = await apiClient.post<ApiResponse<Post>>(`/posts/admin/${id}/unsuspend`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unsuspend post');
    }
    return response.data.data;
  }

  async rejectPost(id: string, reason: string) {
    const response = await apiClient.post<ApiResponse<Post>>(`/posts/admin/${id}/reject`, { reason });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reject post');
    }
    return response.data.data;
  }
}

export const postsService = new PostsService();
export default postsService;

